// ============================================================================
// CONFIG SETTINGS SCREEN
// ============================================================================
// Two-panel editor for the generic Config Framework.
//   - Left: group sidebar (LITIGATION_POLICY, HEARING_POLICY, etc.)
//   - Right: scope tabs (System / Entity / Product) + settings form + save bar
//
// Recsuit hierarchy: SYSTEM (0) → ENTITY (1) → PRODUCT (2). The Entity tab
// auto-hides for single-entity tenants (when no ENTITY scope rows exist).

import React, { useCallback, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import useConfigSettings, { type ScopeTab } from 'hooks/api/useConfigSettings';
import ConfigGroupSidebar from './ConfigGroupSidebar';
import ConfigSettingField from './ConfigSettingField';

interface ConfigSettingsScreenProps {
  tabId?: string;
  moduleName?: string;
}

const SCOPE_LEVEL: Record<ScopeTab, number> = { SYSTEM: 0, ENTITY: 1, PRODUCT: 2 };

const ConfigSettingsScreen: React.FC<ConfigSettingsScreenProps> = () => {
  const cs = useConfigSettings();

  const [pendingScopeChange, setPendingScopeChange] = useState<{
    type: 'group' | 'scope' | 'entity' | 'product' | 'refresh';
    payload: any;
  } | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

  const guardSwitch = useCallback((apply: () => void, kind: typeof pendingScopeChange) => {
    if (cs.hasChanges) {
      setPendingScopeChange(kind);
      setDiscardConfirmOpen(true);
    } else {
      apply();
    }
  }, [cs.hasChanges]);

  const applyPending = useCallback(() => {
    if (!pendingScopeChange) return;
    cs.resetEdits();
    const { type, payload } = pendingScopeChange;
    if (type === 'group') cs.setActiveGroupKey(payload);
    else if (type === 'scope') cs.setScopeType(payload);
    else if (type === 'entity') cs.setSelectedEntityId(payload);
    else if (type === 'product') cs.setSelectedProductId(payload);
    else if (type === 'refresh') cs.refresh();
    setPendingScopeChange(null);
  }, [cs, pendingScopeChange]);

  const handleSelectGroup = useCallback((key: string) => {
    if (key === cs.activeGroupKey) return;
    guardSwitch(() => cs.setActiveGroupKey(key), { type: 'group', payload: key });
  }, [cs, guardSwitch]);

  const handleScopeChange = useCallback((tab: ScopeTab) => {
    if (tab === cs.scopeType) return;
    guardSwitch(() => cs.setScopeType(tab), { type: 'scope', payload: tab });
  }, [cs, guardSwitch]);

  const handleProductChange = useCallback((productId: string | null) => {
    if (productId === cs.selectedProductId) return;
    guardSwitch(() => cs.setSelectedProductId(productId), { type: 'product', payload: productId });
  }, [cs, guardSwitch]);

  const handleSaveClick = useCallback(() => {
    if (!cs.hasChanges) return;
    // PRODUCT scope requires a product picked
    if (cs.scopeType === 'PRODUCT' && !cs.selectedProductId) {
      notificationService.toast.warning('Pick a product before saving overrides.');
      return;
    }
    if (cs.scopeType === 'ENTITY' && !cs.selectedEntityId) {
      notificationService.toast.warning('Pick an entity before saving overrides.');
      return;
    }
    setSaveConfirmOpen(true);
  }, [cs.hasChanges, cs.scopeType, cs.selectedEntityId, cs.selectedProductId]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    const ok = await cs.saveChanges();
    if (ok) {
      notificationService.toast.success('Configuration saved.');
    } else {
      notificationService.toast.error(cs.saveError?.message || 'Failed to save configuration.', 6000);
    }
  }, [cs]);

  const handleDiscardChanges = useCallback(() => {
    cs.resetEdits();
    notificationService.toast.info('Unsaved changes discarded.');
  }, [cs]);

  const handleRefresh = useCallback(() => {
    guardSwitch(() => cs.refresh(), { type: 'refresh', payload: null });
  }, [cs, guardSwitch]);

  // Toolbar actions
  const actionButtons: ActionButton[] = useMemo(() => [
    {
      id: 'save',
      icon: <IconSystem.save size={18} />,
      label: 'Save',
      tooltip: 'Save changes',
      onClick: handleSaveClick,
      variant: 'primary' as any,
    },
    {
      id: 'discard',
      icon: <IconSystem.refresh size={18} />,
      label: 'Discard',
      tooltip: 'Discard pending changes',
      onClick: () => {
        if (cs.hasChanges) {
          setPendingScopeChange({ type: 'refresh', payload: null });
          setDiscardConfirmOpen(true);
        }
      },
      variant: 'icon',
    },
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Reload from server',
      onClick: handleRefresh,
      variant: 'icon',
    },
  ], [handleSaveClick, handleRefresh, cs.hasChanges]);

  // Determine which scope tabs to show based on the active group's max_scope_level
  const maxScopeLevel = useMemo(() => {
    if (cs.definitions.length === 0) return 2;
    return Math.max(...cs.definitions.map(d => d.maxScopeLevel));
  }, [cs.definitions]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Configuration Settings"
        description="Tenant-wide policy settings. SYSTEM defaults apply globally; per-product overrides can be set under the Product tab."
        icon={<IconSystem.settings size={18} />}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          background: 'var(--bg-primary)',
        }}>
          {/* Left: group sidebar */}
          <ConfigGroupSidebar
            groups={cs.groups}
            activeGroupKey={cs.activeGroupKey}
            onSelect={handleSelectGroup}
          />

          {/* Right: scope tabs + settings form */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Scope tab bar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 1rem',
              borderBottom: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
            }}>
              {(['SYSTEM', 'ENTITY', 'PRODUCT'] as ScopeTab[]).map((tab) => {
                const visible = tab === 'SYSTEM' || tab === 'PRODUCT' || cs.showEntityTab;
                if (!visible) return null;
                const allowedAtThisLevel = SCOPE_LEVEL[tab] <= maxScopeLevel;
                if (!allowedAtThisLevel) return null;
                const active = cs.scopeType === tab;
                const label = tab === 'SYSTEM' ? 'System' : tab === 'ENTITY' ? 'Entity' : 'Product';
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => handleScopeChange(tab)}
                    style={{
                      padding: '0.4rem 0.875rem',
                      border: 'none',
                      background: active ? '#2563eb' : 'transparent',
                      color: active ? '#ffffff' : 'var(--text-secondary)',
                      borderRadius: '6px',
                      fontSize: '0.8125rem',
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </button>
                );
              })}

              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                {cs.scopeType === 'PRODUCT' && (
                  <div style={{ minWidth: '260px' }}>
                    <SyutSelect
                      options={[
                        { value: '', label: cs.productsLoading ? 'Loading products…' : '— Select product —' },
                        ...cs.productOptions,
                      ]}
                      value={cs.selectedProductId ?? ''}
                      onChange={(v) => handleProductChange((v as string) || null)}
                      isClearable={false}
                      isDisabled={cs.productsLoading}
                      size="sm"
                      placeholder="— Select product —"
                    />
                  </div>
                )}
                {cs.hasChanges && (
                  <span style={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>
                    Unsaved changes
                  </span>
                )}
              </div>
            </div>

            {/* Settings list */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {cs.activeGroupKey == null && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Select a group from the left.
                </div>
              )}

              {cs.activeGroupKey != null && cs.scopeType === 'PRODUCT' && !cs.selectedProductId && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Select a product from the toolbar above to view / edit per-product overrides.
                </div>
              )}

              {cs.activeGroupKey != null && cs.scopeType === 'ENTITY' && !cs.selectedEntityId && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Entity scope picker not yet wired — single-entity tenants typically skip this tab.
                </div>
              )}

              {cs.valuesLoading && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Loading…
                </div>
              )}

              {!cs.valuesLoading
                && cs.activeGroupKey != null
                && (cs.scopeType === 'SYSTEM' || (cs.scopeType === 'PRODUCT' && cs.selectedProductId) || (cs.scopeType === 'ENTITY' && cs.selectedEntityId))
                && cs.definitions.length === 0 && (
                  <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No definitions in this group.
                  </div>
                )}

              {!cs.valuesLoading
                && cs.activeGroupKey != null
                && cs.definitions.length > 0
                && cs.definitions.map(def => {
                  const eff = cs.effectiveValues.find(e => e.configKey === def.configKey);
                  return (
                    <ConfigSettingField
                      key={def.id}
                      definition={def}
                      effective={eff}
                      scopeType={cs.scopeType}
                      scopeLevel={SCOPE_LEVEL[cs.scopeType]}
                      editedValue={cs.editedValues[def.configKey]?.value}
                      editedListValues={cs.editedValues[def.configKey]?.listValues}
                      isCleared={cs.clearedKeys.has(def.configKey)}
                      onScalarChange={(v) => cs.updateFieldValue(def.configKey, v)}
                      onListChange={(items) => cs.updateListValue(def.configKey, items)}
                      onClearOverride={() => cs.clearOverride(def.configKey)}
                      disabled={cs.saving}
                    />
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {/* Discard pending changes? (group / scope / entity / product / refresh switches) */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Continue and lose them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          if (pendingScopeChange) applyPending();
          else handleDiscardChanges();
        }}
        onCancel={() => {
          setDiscardConfirmOpen(false);
          setPendingScopeChange(null);
        }}
      />

      {/* Confirm save */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={
          cs.scopeType === 'SYSTEM'
            ? 'Save these values as SYSTEM defaults? This affects all entities and products without overrides.'
            : cs.scopeType === 'PRODUCT'
              ? 'Save these values as PRODUCT-level overrides? Other products inheriting from SYSTEM are not affected.'
              : 'Save these values as ENTITY-level overrides?'
        }
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={cs.saving}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
    </div>
  );
};

export default ConfigSettingsScreen;
